import { ReactNode, useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { customEvent } from "goi_common";

import { ArrowRightIcon } from "@/components/DirectionsIcons";
import { toManwon } from "@/utils/formatMoney";
import { RegionParlor } from "@/utils/regionFacilities";

const VISIBLE_STEP = 10;

type SortKey = "price" | "binso";
type FilterKey = "all" | "public" | "parking";

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: "price", label: "비용 낮은순" },
  { key: "binso", label: "빈소 많은순" },
];

const FILTERS: Array<{ key: FilterKey; label: string }> = [
  { key: "all", label: "전체" },
  { key: "public", label: "공설만" },
  { key: "parking", label: "주차 100대 이상" },
];

const ALL_REGIONS = "전체 지역";

interface RegionParlorListProps {
  sigunguName: string;
  /**
   * 이 지역에 장례식장이 소재하는지. false 면 parlors 가 인근 시설 거리순
   * 목록이라 제목과 카드 표기를 인근 기준으로 바꾼다
   */
  hasParlorInRegion?: boolean;
  parlors: RegionParlor[];
  /**
   * 시도 페이지에서만 채워진다. 지역(시군구) 필터 옵션.
   * 비어 있으면 지역 필터 행을 렌더하지 않는다(시군구 페이지에서는 의미가 없다).
   */
  sigunguOptions?: string[];
  /** 개별 장례식장 견적 링크. 크로스도메인이라 platform URL을 만들어 넘겨받는다 */
  buildQuoteHref: (parlor: RegionParlor) => string;
  /** 섹션 맨 아래에 붙일 것. 인근 지역 장례식장 링크가 들어온다 */
  footer?: ReactNode;
}

export const RegionParlorList = ({
  sigunguName,
  hasParlorInRegion = true,
  parlors,
  sigunguOptions = [],
  buildQuoteHref,
  footer,
}: RegionParlorListProps) => {
  const [sort, setSort] = useState<SortKey>("price");
  const [filter, setFilter] = useState<FilterKey>("all");
  const [region, setRegion] = useState(ALL_REGIONS);
  const [expanded, setExpanded] = useState(false);

  const hasRegionFilter = sigunguOptions.length > 1;

  const filtered = useMemo(() => {
    const list = parlors.filter((parlor) => {
      if (region !== ALL_REGIONS && parlor.sigunguName !== region) return false;
      if (filter === "public") return parlor.isPublic;
      if (filter === "parking") return (parlor.parkcnt ?? 0) >= 100;
      return true;
    });

    return list.sort((a, b) => {
      if (sort === "binso") return b.binsoCount - a.binsoCount;
      // 요금 미공개 시설은 뒤로 보낸다
      const left = a.binsoMinCharge ?? Number.POSITIVE_INFINITY;
      const right = b.binsoMinCharge ?? Number.POSITIVE_INFINITY;
      return left - right;
    });
  }, [parlors, sort, filter, region]);

  const shown = expanded ? filtered : filtered.slice(0, VISIBLE_STEP);
  const hiddenCount = filtered.length - shown.length;

  return (
    <section
      className="rg-section"
      id="parlors"
      aria-labelledby="rg-parlors-title"
    >
      <header className="rg-section__head rg-section__head--stack">
        <h2 id="rg-parlors-title" className="rg-section__title">
          {/* 0곳 시군구는 parlors 가 인근 시설이다. "{시군구} 장례식장 N곳" 으로
              쓰면 거짓이 되므로 화장장 섹션과 같은 "인근 포함" 표기를 쓴다 */}
          {hasParlorInRegion
            ? `${sigunguName} 장례식장 `
            : `${sigunguName} 장례식장, 인근 포함 `}
          <span className="rg-accent">{parlors.length}곳</span>
        </h2>
        <p className="rg-section__desc">
          {!hasParlorInRegion &&
            `${sigunguName}에는 장례식장이 없어 가까운 인근 시설을 안내합니다. `}
          빈소 사용료는 각 시설이 공개한 요금표의 최저 항목입니다. 시설마다 기준
          면적과 기간이 달라 실제 비용은 조문객 수, 접객 음식, 장례용품에 따라
          달라집니다.
        </p>
      </header>

      {/* 지역 필터. 시도 페이지에서만 나온다. 이 페이지가 시군구 페이지와 다른 점이다 */}
      {hasRegionFilter && (
        <div className="rg-filterbar" role="group" aria-label="지역 필터">
          {[ALL_REGIONS, ...sigunguOptions].map((option) => (
            <button
              key={option}
              type="button"
              className={`rg-fchip${region === option ? " rg-fchip--on" : ""}`}
              aria-pressed={region === option}
              onClick={() => {
                setRegion(option);
                setExpanded(false);
                customEvent({
                  newGtm: {
                    path: "prlr_region",
                    trigger_and_target: "filter_sigungu",
                  },
                });
              }}
            >
              {option}
            </button>
          ))}
        </div>
      )}

      {/* 필터 칩 행. 가로 스크롤되며 활성 칩만 진하게 */}
      <div className="rg-filterbar" role="group" aria-label="필터">
        {FILTERS.map((option) => (
          <button
            key={option.key}
            type="button"
            className={`rg-fchip${
              filter === option.key ? " rg-fchip--on" : ""
            }`}
            aria-pressed={filter === option.key}
            onClick={() => {
              setFilter(option.key);
              setExpanded(false);
              customEvent({
                newGtm: {
                  path: "prlr_region",
                  trigger_and_target:
                    option.key === "all"
                      ? "filter_all"
                      : option.key === "public"
                      ? "filter_public"
                      : "filter_parking",
                },
              });
            }}
          >
            {option.label}
          </button>
        ))}
      </div>

      {/* 결과 수 + 정렬. 정렬은 칩이 아니라 텍스트 토글로 둔다 */}
      <div className="rg-resultbar">
        <span className="rg-resultbar__count">
          {`${filtered.length.toLocaleString("ko-KR")}개`}
        </span>
        <div className="rg-sortgroup" role="group" aria-label="정렬">
          {SORTS.map((option) => (
            <button
              key={option.key}
              type="button"
              className={`rg-sort${sort === option.key ? " rg-sort--on" : ""}`}
              aria-pressed={sort === option.key}
              onClick={() => {
                setSort(option.key);
                customEvent({
                  newGtm: {
                    path: "prlr_region",
                    trigger_and_target:
                      option.key === "price" ? "sort_price" : "sort_binso",
                  },
                });
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {shown.length === 0 ? (
        <p className="rg-empty">조건에 맞는 장례식장이 없습니다.</p>
      ) : (
        <ul className="rg-cards">
          {shown.map((parlor) => (
            <li key={parlor.id} className="rg-card rg-card--parlor">
              <div className="rg-card__body">
                {parlor.imageUrl && (
                  <Link
                    href={`/directions/${parlor.id}/`}
                    className="rg-thumb"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <Image
                      src={parlor.imageUrl}
                      alt=""
                      width={132}
                      height={99}
                      className="rg-thumb__img"
                      unoptimized
                    />
                  </Link>
                )}
                <div className="rg-card__info">
                  <div className="rg-card__titles">
                    <span
                      className={`rg-badge${
                        parlor.isPublic ? " rg-badge--dark" : ""
                      }`}
                    >
                      {parlor.managedType}
                    </span>
                    {/* 개별 시설 상세로 내부 링크를 흘린다. robots.txt가 /place/는 일부 차단하고
                      /directions/는 전부 허용하므로 크롤 가능한 쪽으로 보낸다 */}
                    <h3 className="rg-card__title">
                      <Link
                        href={`/directions/${parlor.id}/`}
                        className="rg-card__link"
                        onClick={() =>
                          customEvent({
                            newGtm: {
                              path: "prlr_region",
                              trigger_and_target: "link_parlor_detail",
                            },
                          })
                        }
                      >
                        {parlor.companyName}
                      </Link>
                    </h3>
                  </div>

                  <p className="rg-card__meta">
                    {/* 인근 목록은 카드마다 다른 시군구라 지역명이 없으면
                        어디 시설인지 알 수 없다 */}
                    {(hasRegionFilter || !hasParlorInRegion) && (
                      <span className="rg-card__region">
                        {parlor.sigunguName}
                      </span>
                    )}
                    {parlor.address}
                    {parlor.binsoCount > 0 && `, 빈소 ${parlor.binsoCount}실`}
                    {parlor.parkcnt !== null &&
                      parlor.parkcnt > 0 &&
                      `, 주차 ${parlor.parkcnt.toLocaleString("ko-KR")}대`}
                  </p>
                </div>
              </div>

              <div className="rg-card__aside">
                <div className="rg-price">
                  <span className="rg-price__label">빈소 사용료 최저</span>
                  <strong className="rg-price__value">
                    {toManwon(parlor.binsoMinCharge) ?? "문의"}
                  </strong>
                  {parlor.expectedFamily !== null && (
                    <span className="rg-price__sub">
                      {`가족장 예상 ${toManwon(parlor.expectedFamily)}`}
                    </span>
                  )}
                </div>

                <a
                  className="rg-btn rg-btn--primary"
                  href={buildQuoteHref(parlor)}
                  onClick={() =>
                    customEvent({
                      newGtm: {
                        path: "prlr_region",
                        trigger_and_target: `btn_parlor_quote_${parlor.id}`,
                      },
                    })
                  }
                >
                  <span>이 장례식장 비용 알아보기</span>
                  <ArrowRightIcon size={15} />
                </a>
              </div>
            </li>
          ))}
        </ul>
      )}

      {(hiddenCount > 0 || expanded) && filtered.length > VISIBLE_STEP && (
        <button
          type="button"
          className="rg-btn rg-btn--ghost rg-more"
          onClick={() => {
            customEvent({
              newGtm: {
                path: "prlr_region",
                trigger_and_target: expanded ? "btn_fold" : "btn_more",
              },
            });
            setExpanded((prev) => !prev);
          }}
        >
          {expanded ? "접기" : `장례식장 ${hiddenCount}곳 더보기`}
        </button>
      )}

      {footer}
    </section>
  );
};
